import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { toRef, toValue, type MaybeRefOrGetter } from "vue";

import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { SiteQueryKeys } from "@/data/api/site-query-keys";
import { SiteProgrammingRangeQueryKeys } from "@/data/api/v4b/site-programming-range/site-programming-range-query-keys";
import { SiteSectionMutationKeys } from "@/data/api/v4b/site-section/site-section-mutation-keys";
import { V4HSiteId } from "@/data/common/ids";
import { CreateSectionCategoryRequest } from "@/domains/v4b/site-section/site-section.types";

export function useCreateSectionCategoryMutation(siteId: MaybeRefOrGetter<V4HSiteId | undefined>) {
  const v4b = useServices().v4b.clientSdk;
  const queryClient = useQueryClient();
  const _siteId = toRef(siteId);

  return useMutation({
    mutationKey: _siteId.value ? SiteSectionMutationKeys.createCategory(_siteId.value) : undefined,
    mutationFn: (command: CreateSectionCategoryRequest) => {
      const currentSiteId = toValue(siteId);
      if (!currentSiteId) {
        throw new Error("Site ID is required");
      }

      return v4b.siteSectionCategory.create(currentSiteId, command);
    },
    onSettled: async () => {
      const currentSiteId = toValue(siteId);
      if (!currentSiteId) return;

      await queryClient.invalidateQueries({
        queryKey: SiteQueryKeys.sections(currentSiteId),
      });

      await queryClient.removeQueries({
        queryKey: SiteProgrammingRangeQueryKeys.findPlanning(currentSiteId),
      });
    },
    retry: 0,
  });
}
